import {
	ConfirmationStrategy,
	DriftEnv,
	getMarketsAndOraclesForSubscription,
	initialize,
	Wallet,
} from '@drift-labs/sdk';
import {
	EnvironmentConstants,
	Initialize as InitializeCommon,
	UIEnv,
} from '@drift/common';
import { E2EWalletAdapter } from '@jet-lab/e2e-react-adapter';
import { Commitment, Keypair } from '@solana/web3.js';
import { EnvironmentVariables, UserSettings } from './EnvironmentVariableTypes';
import { defaultUserSettings } from './DefaultSettings';
import { PRELAUNCH_MARKET_RESOURCE_CONSTANTS } from '../components/PreLaunchMarketAlerts/PreLaunchMarketConstants';
import { PriorityFee } from '../@types/types';
import { Transaction } from '@solana/web3.js';
import { VersionedTransaction } from '@solana/web3.js';
import {
	isVersionedTransaction,
	WalletNotConnectedError,
} from '@solana/wallet-adapter-base';
import { getNextDelistingTs, parseDelistingConfig } from 'src/utils/delisting';
export * from './EnvironmentVariableTypes';
export * from './DefaultSettings';

const urlHasParam = (param: string) => {
	const urlSearchParams = new URLSearchParams(window.location.search);

	return urlSearchParams.has(param);
};

const getNumericEnvVar = (
	envVar: string | undefined,
	defaultValue: number
): number => {
	if (envVar === undefined || envVar === null) return defaultValue;
	const parsed = Number(envVar);
	return isNaN(parsed) ? defaultValue : parsed;
};

async function signTransaction<T extends Transaction | VersionedTransaction>(
	transaction: T
): Promise<T> {
	// @ts-ignore
	if (!this._underlyingWallet) throw new WalletNotConnectedError();

	if (isVersionedTransaction(transaction)) {
		// @ts-ignore
		transaction.sign([this._underlyingWallet]);
	} else {
		// @ts-ignore
		transaction.sign(this._underlyingWallet);
	}

	return transaction;
}

async function signAllTransactions<
	T extends Transaction | VersionedTransaction,
>(transactions: T[]): Promise<T[]> {
	// @ts-ignore
	if (!this._underlyingWallet) throw new WalletNotConnectedError();

	for (const transaction of transactions) {
		if (isVersionedTransaction(transaction)) {
			// @ts-ignore
			// Sign VersionedTransaction
			transaction.sign([this._underlyingWallet]);
		} else {
			// @ts-ignore
			// Sign regular Transaction
			transaction.sign(this._underlyingWallet);
		}
	}

	return transactions;
}

// overwriting wallet sign functions as it does not support versionedTransactions
E2EWalletAdapter.prototype.signTransaction = signTransaction;
E2EWalletAdapter.prototype.signAllTransactions = signAllTransactions;

const isWindowDefined = () => typeof window !== 'undefined';

export const syncGetCurrentSettings = (): UserSettings | undefined => {
	if (!isWindowDefined()) return undefined;

	const settings = localStorage.getItem('settings');

	let parsedSettings = {};

	if (settings) {
		parsedSettings = JSON.parse(settings);
	}

	const result = {
		...defaultUserSettings,
		...parsedSettings,
	};

	if (urlHasParam('useMainnet')) {
		result.devnetOverride = false;
	}

	return result;
};

// --- Environment Settings
const savedSettings = syncGetCurrentSettings();

// Default to mainnet-beta unless explicitly set to devnet via devnetOverride
const sdkEnv =
	savedSettings?.devnetOverride === true
		? 'devnet'
		: ((process.env.NEXT_PUBLIC_OVERRIDE_CLUSTER ??
				'mainnet-beta') as DriftEnv);

export const sdkConfig = initialize({ env: sdkEnv });
InitializeCommon(sdkEnv);

// default to random rpc to not overload one
const RPC_LIST =
	sdkEnv === 'mainnet-beta'
		? EnvironmentConstants.rpcs.mainnet
		: EnvironmentConstants.rpcs.dev;
const randomIndex = Math.floor(Math.random() * RPC_LIST.length);
const rpcToUse = RPC_LIST[randomIndex];

const historyServerToUse =
	sdkConfig.ENV === 'mainnet-beta' &&
	process.env.NEXT_PUBLIC_FORCE_USE_ENV_HISTORY_SERVER !== 'true'
		? savedSettings?.stagingHistoryServerOverride ||
			process.env.NEXT_PUBLIC_FORCE_USE_STAGING_SERVER
			? EnvironmentConstants.historyServerUrl.staging
			: EnvironmentConstants.historyServerUrl.mainnet
		: process.env.NEXT_PUBLIC_EXCHANGE_HISTORY_SERVER_URL;

const dataApiEnvToUse: UIEnv =
	savedSettings?.stagingDataServerOverride ||
	process.env.NEXT_PUBLIC_FORCE_USE_STAGING_SERVER
		? UIEnv.createStaging()
		: sdkConfig.ENV === 'mainnet-beta'
			? UIEnv.createMainnet()
			: UIEnv.createDevnet();

const dataApiUrlToUse = process.env.NEXT_PUBLIC_DATA_API_URL_OVERRIDE
	? process.env.NEXT_PUBLIC_DATA_API_URL_OVERRIDE
	: dataApiEnvToUse.isStaging
		? EnvironmentConstants.dataServerUrl.staging
		: dataApiEnvToUse.isMainnet
			? EnvironmentConstants.dataServerUrl.mainnet
			: EnvironmentConstants.dataServerUrl.dev;

const dlobHttpServerToUse = process.env.NEXT_PUBLIC_DLOB_SERVER_OVERRIDE
	? process.env.NEXT_PUBLIC_DLOB_SERVER_OVERRIDE
	: savedSettings?.stagingDlobServerOverride
		? EnvironmentConstants.dlobServerHttpUrl.staging
		: sdkConfig.ENV === 'mainnet-beta'
			? EnvironmentConstants.dlobServerHttpUrl.mainnet
			: EnvironmentConstants.dlobServerHttpUrl.dev;

const dlobWsServerToUse = process.env.NEXT_PUBLIC_OVERRIDE_WS_URL
	? process.env.NEXT_PUBLIC_OVERRIDE_WS_URL
	: savedSettings?.stagingDlobServerOverride
		? EnvironmentConstants.dlobServerWsUrl.staging
		: sdkConfig.ENV === 'mainnet-beta'
			? EnvironmentConstants.dlobServerWsUrl.mainnet
			: EnvironmentConstants.dlobServerWsUrl.dev;

const eventsServerToUse = process.env.NEXT_PUBLIC_EVENTS_SERVER_OVERRIDE
	? process.env.NEXT_PUBLIC_EVENTS_SERVER_OVERRIDE
	: sdkConfig.ENV === 'mainnet-beta'
		? EnvironmentConstants.eventsServerUrl.mainnet
		: undefined;

const swiftServerToUse = process.env.NEXT_PUBLIC_SWIFT_SERVER_URL_OVERRIDE
	? process.env.NEXT_PUBLIC_SWIFT_SERVER_URL_OVERRIDE
	: sdkConfig.ENV === 'mainnet-beta'
		? EnvironmentConstants.swiftServerUrl.mainnet
		: EnvironmentConstants.swiftServerUrl.staging;

const DEFAULT_WALLET = new Wallet(new Keypair());

const E2E_TEST_WALLET_SECRET = process.env.NEXT_PUBLIC_E2E_TEST_WALLET_SECRET
	? process.env.NEXT_PUBLIC_E2E_TEST_WALLET_SECRET.split(',').map(Number)
	: undefined;
const E2E_WALLET_KEYPAIR = E2E_TEST_WALLET_SECRET
	? Keypair.fromSecretKey(Uint8Array.from(E2E_TEST_WALLET_SECRET))
	: new Keypair();
const E2E_WALLET = new E2EWalletAdapter({ keypair: E2E_WALLET_KEYPAIR });

const { oracleInfos } = getMarketsAndOraclesForSubscription(sdkConfig.ENV);

const pollingFrequencyMs = getNumericEnvVar(
	process.env.NEXT_PUBLIC_POLLING_FREQUENCY_MS,
	1000
);

const selectedMarketPollingCadenceMs = getNumericEnvVar(
	process.env.NEXT_PUBLIC_SELECTED_MARKET_POLLING_CADENCE_MS,
	1000
);

const activeMarketPollingCadenceMs = getNumericEnvVar(
	process.env.NEXT_PUBLIC_ACTIVE_MARKET_POLLING_CADENCE_MS,
	1000
);

const backgroundMarketPollingCadenceMs = getNumericEnvVar(
	process.env.NEXT_PUBLIC_BACKGROUND_MARKET_POLLING_CADENCE_MS,
	60_000
);

const auctionExpirySlotBuffer = getNumericEnvVar(
	process.env.NEXT_PUBLIC_AUCTION_EXPIRY_SLOT_BUFFER,
	100
);

const orderMaxTsBufferMs = getNumericEnvVar(
	process.env.NEXT_PUBLIC_ORDER_MAX_TS_BUFFER_MS,
	15_000
);

const delistingConfig = parseDelistingConfig(
	process.env.NEXT_PUBLIC_DELISTING_CONFIG
);
const nextDelistingTs = getNextDelistingTs(
	delistingConfig,
	getNumericEnvVar(process.env.NEXT_PUBLIC_NEXT_DELISTING_TS, 0)
);

const defaultCommitmentLevel: Commitment = (() => {
	const commitmentLevel = process.env.NEXT_PUBLIC_DEFAULT_COMMITMENT_LEVEL;
	switch (commitmentLevel) {
		case 'processed':
		case 'confirmed':
		case 'finalized':
		case 'recent':
		case 'single':
		case 'singleGossip':
		case 'root':
		case 'max':
			return commitmentLevel;
		default:
			return 'confirmed';
	}
})();

const Env: EnvironmentVariables = {
	sdkEnv: sdkEnv,
	sdkConfig,
	defaultRpcAddress: rpcToUse.value,
	defaultRpcLabel: rpcToUse.label,
	historyServerUrl: historyServerToUse ?? '',
	dataApiUrl: dataApiUrlToUse,
	dataApiEnvToUse: dataApiEnvToUse,
	dlobWsServerToUse: dlobWsServerToUse,
	eventsServerToUse: eventsServerToUse,
	dlobServerHttpUrl: dlobHttpServerToUse,
	mainnetTermsUrl:
		'https://docs.drift.trade/legal-and-regulations/terms-of-use',
	mainnetDisclaimerUrl:
		'https://docs.drift.trade/legal-and-regulations/disclaimer',
	selectedMarketPollingCadenceMs,
	activeMarketPollingCadenceMs,
	backgroundMarketPollingCadenceMs,
	pollingFrequencyMs: pollingFrequencyMs ?? 1000,
	oracleInfos,
	serumAddress: sdkConfig.SERUM_V3,
	phoenixAddress: sdkConfig.PHOENIX,
	maxAccountsPerWallet: getNumericEnvVar(
		process.env.MAX_ACCOUNTS_PER_WALLET,
		8
	) as number,
	locale: 'en-US',
	magicAuthApiKey: process.env.NEXT_PUBLIC_MAGIC_AUTH_API_KEY as string,
	defaultWallet: DEFAULT_WALLET,
	E2E_WALLET: E2E_WALLET,
	priceDivergenceWarningThreshold: getNumericEnvVar(
		process.env.NEXT_PUBLIC_PRICE_DIVERGENCE_THRESHOLD,
		1
	) as number,
	showErrorLogs: process.env.NEXT_PUBLIC_SHOW_ERROR_LOGS === 'true',
	minInitialDepositValue: getNumericEnvVar(
		process.env.NEXT_PUBLIC_MIN_INITIAL_DEPOSIT_VALUE,
		10
	) as number,
	defaultMakerRebateBps: getNumericEnvVar(
		process.env.NEXT_PUBLIC_MAKER_REBATE_BPS,
		2
	) as number,
	defaultTakerFeeBps: getNumericEnvVar(
		process.env.NEXT_PUBLIC_DEFAULT_TAKER_FEE_BPS,
		10
	) as number,
	metricsLoggingEnabled:
		process.env.NEXT_PUBLIC_ENABLE_METRICS_LOGGING === 'true',
	metricsLoggingSampleRatio: getNumericEnvVar(
		process.env.NEXT_PUBLIC_METRICS_LOGGING_SAMPLE_RATIO,
		0.1
	) as number,
	dynamicPriorityFeeMultiplier: getNumericEnvVar(
		process.env.NEXT_PUBLIC_PRIORITY_FEE_MULTIPLIER,
		1
	),
	uiEssentialUpdateIntervalMs: getNumericEnvVar(
		process.env.NEXT_PUBLIC_UI_ESSENTIAL_UPDATE_INTERVAL_MS,
		1000
	),
	uiNonEssentialUpdateIntervalMs: getNumericEnvVar(
		process.env.NEXT_PUBLIC_UI_NON_ESSENTIAL_UPDATE_INTERVAL_MS,
		3000
	),
	dynamicFeeTargetPercentile: getNumericEnvVar(
		process.env.NEXT_PUBLIC_DYNAMIC_FEE_TARGET_PERCENTILE,
		80
	),
	confirmationStrategy:
		ConfirmationStrategy[
			(process.env.NEXT_PUBLIC_CONFIRMATION_STRATEGY ??
				'Combo') as keyof typeof ConfirmationStrategy
		],
	feeSubscriptionPollingMultiplier: getNumericEnvVar(
		process.env.NEXT_PUBLIC_PRIORITY_FEE_SUBSCRIPTION_POLLING_MULTIPLIER,
		5
	),
	maxAccountsSwapBuffer: getNumericEnvVar(
		process.env.NEXT_PUBLIC_MAX_ACCOUNTS_SWAP_BUFFER,
		3
	) as number,
	driftDialectDappAddress:
		(process.env.NEXT_PUBLIC_DRIFT_DIALECT_DAPP_ADDRESS ?? // Can override the dialect address with this variable
		sdkEnv === 'mainnet-beta')
			? 'Cuvh8Ji4scfzJGekSeADhDQrm67Ne9tFoouyHRA6VkJT' // This is the MAINNET Dapp address
			: '9tNykNRDwnaUQ9f1NDazU113bs6z1SeZLZasf5QVYFiL', // This is the DEVNET Dapp address,
	dialectNotificationsEnabled:
		process.env.NEXT_PUBLIC_DIALECT_NOTIFICATIONS_ENABLED === 'true',
	useSimulatedComputeUnitBudget:
		process.env.NEXT_PUBLIC_USE_SIMULATED_COMPUTE_UNIT_BUDGET === 'true',
	useSimulatedComputeUnitBudgetForFees:
		process.env.NEXT_PUBLIC_USE_SIMULATED_COMPUTE_UNIT_BUDGET_FOR_FEES ===
		'true',
	computeUnitBufferMultiplier: getNumericEnvVar(
		process.env.NEXT_PUBLIC_COMPUTE_UNIT_BUFFER_MULTIPLIER,
		1.2
	) as number,
	minimumPriorityFee: getNumericEnvVar(
		process.env.NEXT_PUBLIC_MINIMUM_PRIORITY_FEE,
		1
	) as PriorityFee,
	txSenderRetryInterval:
		parseInt(process.env.NEXT_PUBLIC_TX_SENDER_RETRY_INTERVAL as string) ||
		undefined,
	initialDataSourceMaxDelay: getNumericEnvVar(
		process.env.NEXT_PUBLIC_INITIAL_DATA_SOURCE_MAX_DELAY,
		5_000
	) as number,
	newDataSourceSubMaxDelay: getNumericEnvVar(
		process.env.NEXT_PUBLIC_NEW_DATA_SOURCE_MAX_DELAY,
		2_000
	) as number,
	upgradeDataSourceDelay: getNumericEnvVar(
		process.env.NEXT_PUBLIC_UPGRADE_DATA_SOURCE_DELAY,
		1 * 60 * 1000
	) as number, // 1 minutes
	placeAndTakeLowerBoundCu: getNumericEnvVar(
		process.env.NEXT_PUBLIC_PLACE_AND_TAKE_LOWER_BOUND_CU,
		400_000
	),
	useCachedBlockhashFetcherInDriftClient: true,
	usdsBonusPerWeek: getNumericEnvVar(
		process.env.NEXT_PUBLIC_USDS_BONUS_PER_WEEK,
		0
	) as number,
	exponentPoolJtoBonusPerWeek: getNumericEnvVar(
		process.env.NEXT_PUBLIC_EXPONENT_POOL_JTO_BONUS_PER_WEEK,
		10000
	) as number,
	eurcBonusPerWeek: getNumericEnvVar(
		process.env.NEXT_PUBLIC_EURC_BONUS_PER_WEEK,
		2100
	) as number,
	syrupUsdcBonusPerWeek: getNumericEnvVar(
		process.env.NEXT_PUBLIC_SYRUP_USDC_BONUS_PER_WEEK,
		0
	) as number,
	lbtcBonusPerWeek: getNumericEnvVar(
		process.env.NEXT_PUBLIC_LBTC_BONUS_PER_WEEK,
		0
	) as number,
	cashBonusPerWeek: getNumericEnvVar(
		process.env.NEXT_PUBLIC_CASH_BONUS_PER_WEEK,
		0
	) as number,
	cashBorrowBonusPerWeek: getNumericEnvVar(
		process.env.NEXT_PUBLIC_CASH_BORROW_BONUS_PER_WEEK,
		0
	) as number,
	showAppKitWallet: process.env.NEXT_PUBLIC_SHOW_APPKIT_WALLET === 'true',
	rpcVersion: undefined,
	rpcsToExclude: process.env.NEXT_PUBLIC_RPCS_TO_EXCLUDE
		? process.env.NEXT_PUBLIC_RPCS_TO_EXCLUDE.split(',')
		: [],
	defaultAuctionDurationPercentage: getNumericEnvVar(
		process.env.NEXT_PUBLIC_DEFAULT_AUCTION_DURATION_PCT,
		33
	) as number,
	perpsMaxHighLeverage: getNumericEnvVar(
		process.env.NEXT_PUBLIC_MAX_LEVERAGE,
		101
	) as number,
	swiftServerUrl: swiftServerToUse,
	// This disables adding the oracle crank ixs to various actions like withdraw, deposit, etc.
	disablePervasiveOracleCrank:
		process.env.NEXT_PUBLIC_DISABLE_PERVASIVE_ORACLE_CRANK === 'true',
	signedMessageOrderSlotBuffer: getNumericEnvVar(
		process.env.NEXT_PUBLIC_SIGNED_MESSAGE_ORDER_SLOT_BUFFER,
		2
	) as number,
	marketBasedAuctionStartPriceOffsetMajor: getNumericEnvVar(
		process.env.NEXT_PUBLIC_MARKET_BASED_AUCTION_START_PRICE_OFFSET_MAJOR,
		0
	) as number,
	marketBasedAuctionStartPriceOffsetNonMajor: getNumericEnvVar(
		process.env.NEXT_PUBLIC_MARKET_BASED_AUCTION_START_PRICE_OFFSET_NON_MAJOR,
		-0.1
	) as number,
	enableLstPool: process.env.NEXT_PUBLIC_ENABLE_LST_POOL === 'true',
	oracleCrankStalenessThreshold: getNumericEnvVar(
		process.env.NEXT_PUBLIC_ORACLE_CRANK_STALENESS_THRESHOLD,
		10
	) as number,
	delistingConfig,
	nextDelistingTs,
	auctionExpirySlotBuffer: auctionExpirySlotBuffer,
	orderMaxTsBufferMs: orderMaxTsBufferMs,
	defaultCommitmentLevel,
	funXyzApiKey: process.env.NEXT_PUBLIC_FUN_XYZ_API_KEY as string,
};

/**
 * Checks if the current UI environment is non-production.
 */
export const isDev = () => {
	return isWindowDefined() && !window.location.href.includes('app.drift.trade');
};

export const isProduction = process.env.VERCEL_ENV === 'production';

export const PRELAUNCH_MARKET_RESOURCES =
	sdkConfig.ENV === 'devnet'
		? PRELAUNCH_MARKET_RESOURCE_CONSTANTS.devnet
		: PRELAUNCH_MARKET_RESOURCE_CONSTANTS.mainnet;

// Environment-dependent market indexes
export const JLP_POOL_JLP_MARKET_INDEX = sdkEnv === 'mainnet-beta' ? 33 : 5;
export const JLP_POOL_USDC_MARKET_INDEX = sdkEnv === 'mainnet-beta' ? 34 : 6;

// Re-export market constants for backward compatibility
export {
	DRIFT_TOKEN_BANK_INDEX,
	USDE_MARKET_INDEX,
	SUSDE_MARKET_INDEX,
	BNSOL_MARKET_INDEX,
	BSOL_MARKET_INDEX,
	USDS_MARKET_INDEX,
	DSOL_MARKET_INDEX,
	JLP_MARKET_INDEX,
	EXPONENT_POOL_JITOSOL_MARKET_INDEX,
	EXPONENT_POOL_JTO_MARKET_INDEX,
	LST_POOL_JITOSOL_MARKET_INDEX,
	EURC_MARKET_INDEX,
	FRAG_SOL_MARKET_INDEX,
	KY_SOL_MARKET_INDEX,
	SYRUP_USDC_MARKET_INDEX,
	LBTC_MARKET_INDEX,
	CASH_MARKET_INDEX,
	PRIORITY_SPOT_MARKETS,
} from 'src/constants/markets';
export default Env;
